import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, trackedAction } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'

import { DateRangeInput, SeasonType, UUID } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, OptionProps } from '@avantstay/backoffice-core'

import { ROUTES } from '@/routes'
import finance from '@/services/arriere/modules/finance'
import { trackError, trackEvent } from '@/tracking/customEventReport'

import { getSeasonUpdateErrorMessage } from '../finance.utils'
import { SeasonTableViewType, SelectionMapValues } from '../screens/shared/Seasons.types'
import { sortSeasonPeriods } from '../season.utils'
import {
  CreateSeasonGroupInput,
  CreateSeasonInput,
  Season,
  SeasonGroup,
  SeasonGroupGridItem,
  SeasonPerGroupPeriodInput,
  SeasonsGridItem,
  UpdateSeasonGroupInput,
  UpdateSeasonInput,
} from '../types/api/seasonTypes'

class _SeasonsStore {
  selectedSeasonGroupId: UUID | undefined = undefined

  selectedSeasonId: UUID | undefined = undefined

  showSeasonGroupsModal = false

  viewTypeParam = computedRouteParam<SeasonTableViewType>('view-type', {
    patterns: [ROUTES.seasons.path, ROUTES.seasonGroups.path],
    defaultValue: SeasonTableViewType.seasonsPerGroup,
  })

  searchTermParam = computedRouteParam<string>('search-term', {
    patterns: [
      ROUTES.seasons.path,
      ROUTES.seasonGroups.path,
      ROUTES.yieldGroups.path,
      ROUTES.yieldMaps.path,
    ],
    defaultValue: '',
  })

  dateRangeFilterParam = computedRouteParam<string>('date', {
    patterns: [ROUTES.seasons.path],
    defaultValue: '',
  })

  showArchivedFilterParam = computedRouteParam<string>('show-archived', {
    patterns: [ROUTES.seasons.path, ROUTES.seasonGroups.path, ROUTES.yieldGroups.path],
    defaultValue: '',
  })

  seasonGroupFilterParam = computedRouteParam<string>('season-group', {
    patterns: [ROUTES.seasons.path, ROUTES.pricingTapeChart.path],
    defaultValue: '',
  })

  seasonTypeFilterParam = computedRouteParam<string>('season-type', {
    patterns: [ROUTES.seasons.path],
    defaultValue: '',
  })

  constructor() {
    makeObservable(this, {
      selectedSeasonGroupId: observable,
      selectedSeasonId: observable,
      showSeasonGroupsModal: observable,
      allSeasons: computed,
      allSeasonGroups: computed({ keepAlive: true }),
      seasonsOptions: computed,
      seasonGroupMapValues: computed,
      seasonGroupsOptions: computed,
      selectedSeason: computed,
      selectedSeasonGroup: computed,
      setViewTypeParam: action.bound,
      setSearchTermParam: action.bound,
      setDateRangeFilter: action.bound,
      setShowArchivedFilter: action.bound,
      setSeasonGroupFilter: action.bound,
      setSeasonTypeFilter: action.bound,
      setSelectedSeasonGroupId: action.bound,
      setSelectedSeasonId: action.bound,
      setShowSeasonGroupsModal: action.bound,
      reloadSeasonGrid: action.bound,
    })

    this.archiveSeasonGroup = trackedAction<any>(this.archiveSeasonGroup.bind(this))
    this.updateSeasonGroup = trackedAction<any>(this.updateSeasonGroup.bind(this))
    this.createSeasonGroup = trackedAction<any>(this.createSeasonGroup.bind(this))
    this.archiveSeason = trackedAction<any>(this.archiveSeason.bind(this))
    this.deleteSeason = trackedAction<any>(this.deleteSeason.bind(this))
    this.duplicatePeriod = trackedAction<any>(this.duplicatePeriod.bind(this))
    this.updateSeason = trackedAction<any>(this.updateSeason.bind(this))
    this.createSeason = trackedAction<any>(this.createSeason.bind(this))
  }

  get allSeasons(): Promise<SeasonsGridItem[]> {
    dependsOn(
      this.reloadSeasonGrid,
      this.updateSeason,
      this.deleteSeason,
      this.archiveSeason,
      this.duplicatePeriod,
      this.createSeason,
    )

    if (this.viewTypeParam.get() === SeasonTableViewType.allSeasons) {
      return finance.getAllSeasons().then(seasons => {
        if (!seasons) return []

        const sortedSeasons = sortSeasonPeriods(seasons)
        return sortedSeasons
      })
    }
    const groupId = this.seasonGroupFilterParam.get()
    if (!groupId) return Promise.resolve([])
    return finance.getSeasonsPerGroup(groupId).then(seasons => {
      const sortedSeasons = sortSeasonPeriods(seasons)
      return sortedSeasons
    })
  }

  get allSeasonGroups(): Promise<SeasonGroupGridItem[]> {
    dependsOn(this.createSeasonGroup, this.updateSeasonGroup, this.archiveSeasonGroup)
    return finance
      .getSeasonGroups()
      .then(groups => groups.sort((a, b) => a.name.localeCompare(b.name)))
  }

  get seasonsOptions(): Promise<OptionProps<UUID>[]> {
    return this.allSeasons.then(seasons =>
      seasons
        .filter(season => !season.isArchived)
        .map(season => ({ value: season.id, label: season.name })),
    )
  }

  get seasonGroupMapValues(): Promise<SelectionMapValues[]> {
    return this.allSeasonGroups.then(groups =>
      groups.filter(group => !group.isArchived).map(({ name, id }) => ({ name, id })),
    )
  }

  get seasonGroupsOptions(): Promise<OptionProps<string>[]> {
    return this.allSeasonGroups.then(groups =>
      groups.filter(group => !group.isArchived).map(({ name, id }) => ({ label: name, value: id })),
    )
  }

  get selectedSeason(): Promise<Season | undefined> {
    void [this.selectedSeasonId]
    if (!this.selectedSeasonId) return Promise.resolve(undefined)
    const seasonGroupFilterParam = this.seasonGroupFilterParam.get()
    const seasonId = this.selectedSeasonId
    if (seasonGroupFilterParam) return finance.getSelectedSeasonPerGroup(seasonId)
    return finance.getSeasonById(seasonId)
  }

  get selectedSeasonGroup(): Promise<SeasonGroup | undefined> {
    void [this.selectedSeasonGroupId]
    if (!this.selectedSeasonGroupId) return Promise.resolve(undefined)
    return finance.getSeasonGroupById(this.selectedSeasonGroupId)
  }

  setViewTypeParam = (viewType?: SeasonTableViewType): void => {
    this.viewTypeParam.replace(viewType || undefined)
  }

  setSearchTermParam = (searchTerm?: string): void => {
    this.searchTermParam.replace(searchTerm || undefined)
  }

  setDateRangeFilter = (date?: string): void => {
    this.dateRangeFilterParam.replace(date || undefined)
  }

  setShowArchivedFilter = (value?: string): void => {
    this.showArchivedFilterParam.replace(value || undefined)
  }

  setSeasonGroupFilter = (id?: UUID): void => {
    this.seasonGroupFilterParam.replace(id || undefined)
  }

  setSeasonTypeFilter = (type: SeasonType | string): void => {
    this.seasonTypeFilterParam.replace(type === 'all' ? undefined : type)
  }

  setSelectedSeasonGroupId = (id?: UUID): void => {
    this.selectedSeasonGroupId = id
  }

  setSelectedSeasonId = (seasonId?: UUID): void => {
    this.selectedSeasonId = seasonId
  }

  setShowSeasonGroupsModal = (value: boolean): void => {
    this.showSeasonGroupsModal = value
  }

  reloadSeasonGrid = () => {
    return true
  }

  async createSeason(createSeasonInput: CreateSeasonInput) {
    trackEvent('Trying to create new Season', { createSeasonInput })
    try {
      await finance.createSeason(createSeasonInput).then(() => {
        trackEvent('New Season Created successfully', { createSeasonInput })
        Alert.Success('Season has been successfully created.', { title: 'Created Season' })
        this.reloadSeasonGrid()
      })
    } catch (errorMessage) {
      trackError('Error creating new Season', { createSeasonInput, errorMessage })
      Alert.Error(`Error: ${errorMessage}`)
    }
  }

  async updateSeason(updateSeasonInput: UpdateSeasonInput) {
    if (this.viewTypeParam.get() === SeasonTableViewType.allSeasons) {
      try {
        await finance.updateSeason(updateSeasonInput).then(() => {
          Alert.Success('Season has been successfully updated.', { title: 'Updated Season' })
          this.reloadSeasonGrid()
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (errorMessage: any) {
        Alert.Error(`Error: ${getSeasonUpdateErrorMessage(errorMessage?.message)}`)
      }
    } else {
      const { periods, description, id: seasonId } = updateSeasonInput
      const periodsToUpdate = [] as unknown as [SeasonPerGroupPeriodInput]
      periods.forEach(({ id, minStay, checkInAllowed, checkOutAllowed, dateRange }) =>
        periodsToUpdate.push({
          id: id as string,
          minStay,
          checkInAllowed,
          checkOutAllowed,
          dateRange,
        }),
      )

      try {
        await finance.updateSeasonPerGroup(seasonId, periodsToUpdate, description).then(() => {
          Alert.Success('Season has been successfully updated.', { title: 'Updated Season' })
          this.reloadSeasonGrid()
        })
      } catch (errorMessage) {
        Alert.Error(`Error: ${errorMessage}`)
      }
    }
  }

  async duplicatePeriod(id: UUID, newDateRange: DateRangeInput) {
    return finance.duplicatePeriod(id, newDateRange).then(response => {
      if (response) {
        Alert.Success('Period has been successfully duplicated.', { title: 'Period Duplicated' })
      } else {
        Alert.Error('There was an error duplicating this Period.')
      }
      return response
    })
  }

  async deleteSeason(id: UUID) {
    return finance.deleteSeason(id).then(response => {
      if (response) {
        Alert.Success('Season has been successfully deleted.', { title: 'Deleted Season' })
      } else {
        Alert.Error('There was an error deleting this Season.')
      }
    })
  }

  async archiveSeason(id: UUID) {
    return finance.archiveSeason(id).then(response => {
      if (response) {
        Alert.Success('Season has been successfully archived.', { title: 'Archive Season' })
      } else {
        Alert.Error('There was an error archiving this Season.')
      }
    })
  }

  async createSeasonGroup(createSeasonGroupInput: CreateSeasonGroupInput) {
    return finance.createSeasonGroup(createSeasonGroupInput).then(response => {
      if (response) {
        Alert.Success('Season Group has been successfully created.', {
          title: 'Created Season Group',
        })
      } else {
        Alert.Error('There was an error creating this Season Group.')
      }
    })
  }

  async updateSeasonGroup(updateSeasonGroupInput: UpdateSeasonGroupInput) {
    return finance.updateSeasonGroup(updateSeasonGroupInput).then(response => {
      if (response) {
        Alert.Success('Season Group has been successfully updated.', {
          title: 'Updated Season Group',
        })
      } else {
        Alert.Error('There was an error updating this Season Group.')
      }
    })
  }

  async archiveSeasonGroup(id: UUID) {
    return finance.archiveSeasonGroup(id).then(response => {
      if (response) {
        Alert.Success('Season Group has been successfully archived.', {
          title: 'Archived Season Group',
        })
      } else {
        Alert.Error('There was an error archiving this Season Group.')
      }
    })
  }
}

export const SeasonsStore = new _SeasonsStore()
